/**
 * 운전이야기 글 목록·상세 쿼리 키와 서버 프리페치.
 * 서버 프리페치(page.tsx)와 클라이언트 훅(article.query.ts)이 같은 키를 써야
 * 하이드레이션이 맞물린다. 어긋나면 곧바로 다시 요청해 서버가 실은 본문이 버려진다.
 *
 * 목록·본문을 서버에서 받아 두지 않으면 크롤러가 받는 HTML에는 "로딩중..."과
 * 빈 본문만 남는다. 구글은 렌더링으로 복구하지만 네이버는 JS 렌더링이 제한적이다.
 */
import {
  getArticleList,
  getMainTopArticleList,
} from '@/services/article.service';
import { ApiResponse, QUERY_KEYS } from '@/services/base-api';
import {
  ArticleEntity,
  GetArticleListParams,
} from '@/services/types/article.type';
import { QueryClient } from '@tanstack/react-query';

/** 게시판 목록(/story/blog 등) 한 페이지 글 수 */
export const STORY_LIST_LIMIT = 6;
/** 운전이야기 메인(/story)의 게시판별 미리보기 글 수 */
export const STORY_PREVIEW_LIMIT = 3;

export function articleListQueryKey(params?: GetArticleListParams) {
  return [QUERY_KEYS.ARTICLE, QUERY_KEYS.LIST, params] as const;
}

export function mainTopArticleListQueryKey() {
  return [QUERY_KEYS.ARTICLE, QUERY_KEYS.LIST, 'main-top'] as const;
}

export function articleDetailQueryKey(id: number) {
  return [QUERY_KEYS.ARTICLE, QUERY_KEYS.DETAIL, id] as const;
}

/** URL의 page 쿼리를 목록 페이지 번호로. 클라이언트 ListContainer와 같은 규칙이다. */
export function parseListPage(page: string | string[] | undefined) {
  return parseInt((Array.isArray(page) ? page[0] : page) || '1', 10);
}

/** 글 목록을 서버 쿼리 캐시에 넣는다. 실패하면 비워 두고 클라이언트가 다시 받는다. */
export function prefetchArticleList(
  queryClient: QueryClient,
  params: GetArticleListParams,
) {
  return queryClient.prefetchQuery({
    queryKey: articleListQueryKey(params),
    queryFn: () => getArticleList(params),
  });
}

export function prefetchMainTopArticleList(queryClient: QueryClient) {
  return queryClient.prefetchQuery({
    queryKey: mainTopArticleListQueryKey(),
    queryFn: getMainTopArticleList,
  });
}

/**
 * 글 상세 조회 결과.
 * - ok: 상세를 받았다. 서버 쿼리 캐시에 넣어 본문을 HTML에 싣는다.
 * - notFound: 글이 없다(API 404). 페이지를 열지 않고 404를 돌려준다.
 * - unavailable: 일시적 장애 등으로 못 받았다. 페이지는 열고 클라이언트가 다시 받는다.
 */
export type ArticleDetailResult =
  | { kind: 'ok'; data: ApiResponse<ArticleEntity> }
  | { kind: 'notFound' }
  | { kind: 'unavailable' };

/**
 * 글 상세(/v1/article/{id}). 클라이언트 useArticleDetail과 같은 ApiResponse 형태로 돌려준다.
 * 같은 URL·옵션의 fetch는 Next 메모이제이션으로 한 번만 요청된다
 * (generateMetadata와 본문이 함께 불러도 요청은 한 번이다).
 */
export async function fetchArticleDetail(
  articleId: number,
): Promise<ArticleDetailResult> {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/v1/article/${articleId}`,
      {
        cache: 'force-cache',
        next: {
          revalidate: 86400,
          tags: ['article', `article-${articleId}`],
        },
      },
    );
    if (response.status === 404) return { kind: 'notFound' };
    // 그 밖의 오류는 일시적 장애일 수 있어 페이지를 닫지 않는다.
    if (!response.ok) return { kind: 'unavailable' };

    const result = await response.json();
    if (!result?.data) return { kind: 'unavailable' };

    return {
      kind: 'ok',
      data: { status: 200, message: 'OK', data: result.data as ArticleEntity },
    };
  } catch {
    return { kind: 'unavailable' };
  }
}

/** 글 상세를 받아 서버 쿼리 캐시에 넣는다. 조회 결과도 돌려준다(404·JSON-LD·FAQ 용). */
export async function prefetchArticleDetail(
  queryClient: QueryClient,
  articleId: number,
): Promise<ArticleDetailResult> {
  const detail = await fetchArticleDetail(articleId);
  if (detail.kind === 'ok') {
    queryClient.setQueryData(articleDetailQueryKey(articleId), detail.data);
  }
  return detail;
}
